import type { Task, TaskAdd, TaskUpdate } from "../models/task";
import { createCrudStoreBundle } from "./crud.store";
import { type ApiClient, runStoreAction } from "./shared";

/** API task row; extras allowed beyond parsed Task fields. */
export type TaskEntity = Task & {
	readonly [key: string]: unknown;
};

export type TasksQueryParams = {
	readonly $filter: string;
	readonly $orderby?: string;
	readonly $skip?: number;
	readonly $top?: number;
};

export const createTasksStore = (apiClient: ApiClient) => {
	const bundle = createCrudStoreBundle<TaskEntity, TaskAdd, TaskUpdate>({
		apiClient,
		entityPath: "tasks",
	});

	const query = async (params: TasksQueryParams): Promise<readonly TaskEntity[]> => {
		const state = bundle.useStore.getState();

		return runStoreAction(state.setLoading, state.setError, async () => {
			const query: Record<string, string | number> = {
				$filter: params.$filter,
				$skip: params.$skip ?? 0,
			};
			if (params.$orderby) {
				query.$orderby = params.$orderby;
			}
			if (params.$top !== undefined) {
				query.$top = params.$top;
			}
			const tasks = await apiClient.get<TaskEntity[]>("/get/tasks", query);
			bundle.useStore.getState().upsertMany(tasks);
			return tasks;
		});
	};

	const fetchByProject = async (projectId: string, showCompletedTasks?: boolean): Promise<readonly TaskEntity[]> => {
		const filter = showCompletedTasks
			? `project__id eq '${projectId}'`
			: `project__id eq '${projectId}' and completed eq false`;
		return query({ $filter: filter });
	};

	return {
		useStore: bundle.useStore,
		actions: {
			...bundle.actions,
			fetchByProject,
			query,
		},
	};
};
